
import Vue from 'vue';
import { namespace } from 'vuex-class';
import { Component, Prop } from 'vue-property-decorator';

import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import CompetitionCard from '@/components/sports/all/CompetitionCard.vue';

import {
	GetSportCompetitionsDocument,
	GetSportCompetitionsQueryVariables,
} from '@/pages/graphql/queries/GetSportCompetitions.query';

import type { SportPageType } from '@/interfaces/sport-graphql';
import { SportCompetitionsConnectionV2 } from '@/@types/graphql-types';

const Language = namespace('language');

@Component({
	name: 'CompetitionList',
	components: {
		HiddenHorizontalScrollbar,
		CompetitionCard,
	},
	apollo: {
		competitionsData: {
			query: GetSportCompetitionsDocument,
			variables(): GetSportCompetitionsQueryVariables {
				return this.queryVariables;
			},
			update(data) {
				return data.sportCompetitionsV2;
			},
			skip() {
				return this.$route.name !== 'app.sports.all';
			},
		},
	},
})
export default class CompetitionList extends Vue {
	@Prop() sportPageType: SportPageType;

	competitionsData: SportCompetitionsConnectionV2 = {} as SportCompetitionsConnectionV2;

	// For Horizontal Scroll closure
	isReady = true;

	@Language.Getter language: string;
	@Language.Getter country: string;

	get queryVariables() {
		const today = new Date();
		today.setUTCHours(0, 0, 0, 0);

		return {
			country: this.country,
			language: this.language,
			first: 50,
			after: '',
			filter: {
				activeOn: today.toISOString(),
			},
		};
	}

	get competitions() {
		const { edges } = this.competitionsData;
		if (edges == null || edges.length === 0) return [];

		return edges.map(competition => competition?.node);
	}

	onLoadNext() {
		const pageInfo = this.competitionsData.pageInfo;

		if (!pageInfo?.hasNextPage) return;
		if (!this.isReady) return;

		this.isReady = false;
		this.$apollo.queries.competitionsData
			.fetchMore({
				variables: {
					...this.queryVariables,
					after: pageInfo?.endCursor,
				},
				updateQuery(previousResult, { fetchMoreResult }) {
					const newCompetitions = fetchMoreResult.sportCompetitionsV2.edges;
					const newPageInfo = fetchMoreResult.sportCompetitionsV2.pageInfo;

					return {
						sportCompetitionsV2: {
							__typename: previousResult.sportCompetitionsV2.__typename,
							totalCount: previousResult.sportCompetitionsV2.totalCount,
							edges: [...previousResult.sportCompetitionsV2.edges, ...newCompetitions],
							pageInfo: newPageInfo,
						},
					};
				},
			})
			.finally(() => (this.isReady = true));
	}
}
